import ActionButtons from './ActionButtons';
import Details from './Details';
import FlightCard from './FlightCard';
import ReservationStatus from './ReservationStatus';
import Summary from './Summary';
import TxpImage from './TxpImage';
import TailoredActionName from '../store/schema/telemetry/TailoredActionName';
import logActionDatapoint from '../utils/telemetry/logActionDatapoint';
import isEntityTypeEditable from '../utils/view/isEntityTypeEditable';
import isInputTextValid from '../utils/view/isInputTextValid';
import * as React from 'react';
import TxpConfiguration from 'txp-data/lib/schema/configSchema/TxpConfiguration';
import ActivityType from 'txp-data/lib/schema/innerSchema/ActivityType';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import { CardMode } from 'txp-data/lib/schema/viewSchema/CardMode';
import FlightCardItem from 'txp-data/lib/schema/viewSchema/FlightCardItem';
import ReservationCardItem from 'txp-data/lib/schema/viewSchema/ReservationCardItem';
import TxpStyle from 'txp-data/lib/schema/viewSchema/TxpStyle';
import LogNames from 'txp-data/lib/schema/telemetry/LogNames';
import { DatapointStatus } from 'owa-analytics';
import TxpDataRow from 'txp-data/lib/schema/viewSchema/TxpDataRow';
import getCustomDataForDirtyFields from '../utils/telemetry/getCustomDataForDirtyFields';
import PostalAddress from 'txp-data/lib/schema/innerSchema/PostalAddress';
import getDetailsEditableField from '../utils/view/getDetailsEditableField';

let styles = require('./Txp.scss');
let classNames = require('classnames');

export interface CardState {
    cardMode: CardMode;
}

export interface CardProps {
    cardItem: CardItem;
    txpStyle: TxpStyle;
    txpConfiguration: TxpConfiguration;
    isSpinnerShown?: boolean;
    onSpinnerStateChanged?: (state: boolean) => void;
}

export default class CardComponent extends React.Component<CardProps, CardState> {
    private editableLocation: PostalAddress = this.props.cardItem.postalAddress;
    private summaryEditableField: string = this.props.cardItem.summaryData.firstLine.text;
    private cardItemAriaText: string = this.props.cardItem.ariaText;
    private displayAddress: string;
    private errorMessage: string;

    constructor(props: CardProps) {
        super(props);

        this.state = {
            cardMode: CardMode.ReadOnly,
        };

        // WI 43487 - once server will support displayAddress, we wont need to populate it.
        this.setDisplayAddress(this.props.cardItem.postalAddress);
    }

    componentWillReceiveProps(newProps: CardProps) {
        let newPropsLocationEditableField = newProps.cardItem.postalAddress;
        let newPropsSummaryEditableField = newProps.cardItem.summaryData.firstLine.text;

        if (this.props.cardItem.postalAddress != newPropsLocationEditableField) {
            this.editableLocation = newPropsLocationEditableField;
            this.setDisplayAddress(newProps.cardItem.postalAddress);
        }

        if (this.props.cardItem.summaryData.firstLine.text != newPropsSummaryEditableField) {
            this.summaryEditableField = newPropsSummaryEditableField;
        }
    }

    render() {
        let {
            cardItem,
            txpStyle,
            txpConfiguration,
            isSpinnerShown,
            onSpinnerStateChanged,
        } = this.props;
        let { txpImages } = txpConfiguration;

        let { cardMode } = this.state;
        let inEditMode: boolean = cardMode != CardMode.ReadOnly;
        let isSummaryEditControlEnabled: boolean = false;
        let isDetailsEditControlEnabled: boolean = false;
        let isButtonsEditControlEnabled: boolean = false;
        let isCardTypeEditable: boolean =
            txpConfiguration.isEditableCardEnabled && isEntityTypeEditable(cardItem.entityType);

        if (inEditMode && isCardTypeEditable) {
            isSummaryEditControlEnabled = !!txpConfiguration.getSummaryEditControl;
            isDetailsEditControlEnabled = !!txpConfiguration.getDetailsEditControl;
            isButtonsEditControlEnabled = !!txpConfiguration.getButtonsEditControl;
        }

        let isFlightCard = cardItem.activityType == ActivityType.FlightDeparture;
        let detailsLines = cardItem.detailsData;
        let summaryLines = cardItem.summaryData;
        let expandedCardClassNames = [styles.expandedCard];
        let reservationStatus = (cardItem as ReservationCardItem).reservationStatus;
        let isReservationStatusShown = reservationStatus && reservationStatus.text;
        let flightAndDetailsWrapperClassNames = [
            styles.commonFlightAndDetailsWrapper,
            'allowTextSelection',
        ];

        let detailContainerClassNames = [styles.detailsContainer];
        if (txpStyle.cardDetailsContainer) {
            detailContainerClassNames.push(txpStyle.cardDetailsContainer);
        }

        if (txpStyle.cardContainer) {
            expandedCardClassNames.push(txpStyle.cardContainer);
        }

        if (isFlightCard) {
            flightAndDetailsWrapperClassNames.push(txpStyle.flight);
        }

        if (isReservationStatusShown) {
            flightAndDetailsWrapperClassNames.push(styles.flightAndDetailsWithReservationStatus);
        }

        return (
            <div className={styles.containerCard}>
                <div className={classNames(expandedCardClassNames)}>
                    <div className={txpStyle.cardSummaryContainer} role="listitem">
                        {cardItem.entityImage && (
                            <TxpImage
                                entityImage={cardItem.entityImage}
                                txpImages={txpImages}
                                txpStyle={txpStyle}
                            />
                        )}
                        {!isSummaryEditControlEnabled && (
                            <Summary
                                entityIcon={cardItem.entityIcon}
                                lines={[
                                    this.getEditableField(
                                        this.summaryEditableField,
                                        summaryLines.firstLine
                                    ),
                                    summaryLines.secondLine,
                                    summaryLines.thirdLine,
                                ]}
                                txpStyle={txpStyle}
                                flightSummaryLine={summaryLines.fourthLine}
                                isEditableEntity={isCardTypeEditable}
                            />
                        )}
                        {isSummaryEditControlEnabled && (
                            <>
                                {txpConfiguration.getSummaryEditControl(
                                    cardMode,
                                    [summaryLines.secondLine, summaryLines.thirdLine],
                                    this.summaryEditableField,
                                    cardItem.summaryData.firstLine,
                                    this.onSummaryValueChanged
                                )}
                            </>
                        )}
                    </div>

                    <div
                        className={classNames(detailContainerClassNames)}
                        aria-label={this.cardItemAriaText}
                        role="listitem">
                        <div className={classNames(flightAndDetailsWrapperClassNames)}>
                            {isReservationStatusShown && (
                                <ReservationStatus status={reservationStatus} txpStyle={txpStyle} />
                            )}
                            {isFlightCard && (
                                <FlightCard
                                    txpStyle={txpStyle}
                                    flightCardItem={cardItem as FlightCardItem}
                                    txpImages={txpImages}
                                />
                            )}
                            {!isFlightCard && (
                                <div>
                                    {!isDetailsEditControlEnabled && (
                                        <Details
                                            lines={[
                                                detailsLines.firstLine,
                                                detailsLines.secondLine,
                                                this.getEditableField(
                                                    this.displayAddress,
                                                    detailsLines.thirdLine
                                                ),
                                                detailsLines.fourthLine,
                                                detailsLines.fifthLine,
                                            ]}
                                            txpStyle={txpStyle}
                                            isEditableEntity={isCardTypeEditable}
                                        />
                                    )}
                                    {isDetailsEditControlEnabled && (
                                        <>
                                            {txpConfiguration.getDetailsEditControl(
                                                cardMode,
                                                txpStyle,
                                                [detailsLines.firstLine, detailsLines.secondLine],
                                                this.displayAddress,
                                                cardItem.detailsData &&
                                                    cardItem.detailsData.thirdLine,
                                                cardItem.postalAddress,
                                                this.onDetailsValueChanged,
                                                this.onTextLocationChanged,
                                                txpConfiguration.getDisplayAddress
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {txpConfiguration.getLocationControl && (
                            <>
                                {txpConfiguration.getLocationControl(
                                    cardItem.activityType !== ActivityType.ParcelDelivery
                                )}
                            </>
                        )}
                        {txpConfiguration.showActionButtons && !isButtonsEditControlEnabled && (
                            <ActionButtons
                                cardItem={cardItem}
                                txpConfiguration={txpConfiguration}
                                txpStyle={txpStyle}
                                isSpinnerShown={isSpinnerShown}
                                onSpinnerStateChanged={onSpinnerStateChanged}
                                onActionButtonClicked={this.setEditState}
                            />
                        )}
                        {isButtonsEditControlEnabled && (
                            <>
                                {txpConfiguration.getButtonsEditControl(
                                    this.onSaveClicked,
                                    this.onDiscardClicked,
                                    cardMode,
                                    this.errorMessage
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private onSaveClicked = () => {
        let { cardItem } = this.props;
        let { getPerfDatapointLogger } = this.props.txpConfiguration;
        let perfDatapoint;

        this.setState({
            cardMode: CardMode.Process,
        });

        if (getPerfDatapointLogger) {
            perfDatapoint = getPerfDatapointLogger(TailoredActionName.SaveCard);
            perfDatapoint.addCustomData({ [LogNames.activityType]: cardItem.activityType });
            perfDatapoint.addCustomData(
                getCustomDataForDirtyFields(
                    cardItem,
                    this.summaryEditableField,
                    this.displayAddress
                )
            );
        }

        this.props.txpConfiguration.onSaveEditableCard(
            cardItem,
            perfDatapoint,
            this.summaryEditableField,
            this.editableLocation,
            (clientErrorMessage: string, status: DatapointStatus, error?: Error) => {
                this.errorMessage = clientErrorMessage;
                this.setState({
                    cardMode: CardMode.ServerError,
                });
                perfDatapoint.endWithError(status, error);
            },
            () => {
                this.displayAddress = this.props.txpConfiguration.getDisplayAddress(
                    this.editableLocation
                );

                this.cardItemAriaText =
                    this.props.txpConfiguration.getAriaText(cardItem, [this.displayAddress]) ||
                    this.cardItemAriaText;
                this.setState({
                    cardMode: CardMode.ReadOnly,
                });
                perfDatapoint.end();
            }
        );
    };

    private checkValidInputs = () => {
        let { summaryData, detailsData } = this.props.cardItem;
        if (
            isInputTextValid(this.summaryEditableField, summaryData.firstLine.isRequired) &&
            isInputTextValid(
                this.displayAddress,
                detailsData.thirdLine && detailsData.thirdLine.isRequired
            )
        ) {
            this.setState({
                cardMode: CardMode.Edit,
            });
        } else {
            this.setState({
                cardMode: CardMode.InvalidInput,
            });
        }
    };

    private onDiscardClicked = () => {
        logActionDatapoint(
            TailoredActionName.DiscardCard,
            this.props.txpConfiguration.datapointLogger,
            this.props.cardItem.activityType,
            getCustomDataForDirtyFields(
                this.props.cardItem,
                this.summaryEditableField,
                this.displayAddress
            )
        );
        this.initializeFields();
        this.setState({
            cardMode: CardMode.ReadOnly,
        });
    };

    private onDetailsValueChanged = async (location: PostalAddress) => {
        this.editableLocation = location;
        this.displayAddress =
            location.displayAddress ||
            (await this.props.txpConfiguration.getDisplayAddress(location));
        this.checkValidInputs();
    };

    private onTextLocationChanged = (stringAddress: string) => {
        this.displayAddress = stringAddress;
        this.checkValidInputs();
    };

    private onSummaryValueChanged = (value: string) => {
        this.summaryEditableField = value;
        this.checkValidInputs();
    };

    private setEditState = () => {
        if (isEntityTypeEditable(this.props.cardItem.entityType)) {
            this.editableLocation = this.props.cardItem.postalAddress;
            this.summaryEditableField = this.props.cardItem.summaryData.firstLine.text;
            this.checkValidInputs();
        }
    };

    private initializeFields = () => {
        let { cardItem } = this.props;
        this.editableLocation = cardItem.postalAddress;
        this.summaryEditableField = cardItem.summaryData.firstLine.text;
        this.displayAddress = this.props.txpConfiguration.getDisplayAddress(
            this.props.cardItem.postalAddress
        );
    };

    private getEditableField = (editableField: string, txpDataRow: TxpDataRow): TxpDataRow => {
        if (txpDataRow) {
            txpDataRow.text = editableField || txpDataRow.text;
        }
        return txpDataRow;
    };

    private setDisplayAddress = (postalAddress: PostalAddress) => {
        this.displayAddress = this.props.txpConfiguration.getDisplayAddress
            ? this.props.txpConfiguration.getDisplayAddress(postalAddress)
            : getDetailsEditableField(this.props.cardItem.detailsData);
    };
}
